import { useState, useCallback } from 'react';
import {
  Container,
  Stack,
  Typography,
  Grid,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { BiCloudUpload, BiCloudDownload } from 'react-icons/bi';
import { AiFillEdit } from 'react-icons/ai';
import { CSVDownload } from 'react-csv';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import prospectService from '../services/prospectService';
import Loader from '../Global/Loader';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import UploadLeadProspectTable from './UploadLeadProspectTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';

const { DownloadProspectTemplate, ProspectUpload } = prospectService;

function UploadLeadProspect() {
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const [showTable, setShowTable] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [template, setTemplate] = useState({ headers: null, data: null });
  const [ui, setUi] = useState({
    open: false,
    msg: '',
    fileName: '',
    loader: false,
    apiError: false,
    errorMsg: ''
  });

  const handleDownloadTemplate = useCallback(async () => {
    const res = await DownloadProspectTemplate(LoginData?.jwt);
    setTemplate({ headers: res.headers, data: res.data });
  }, [LoginData]);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const extension = file.name.split('.').pop().toLowerCase();
    if (['xlsx', 'csv', 'xls'].includes(extension)) {
      setUploadFile(file);
      setUi((prev) => ({ ...prev, fileName: file.name }));
    } else {
      setUi((prev) => ({ ...prev, open: true, msg: 'Please add only xlsx/csv/xls file only' }));
    }
  }, []);

  return (
    <Page title="Kpros by Kalolytic Solutions || Prospect">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Upload Leads" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Scrollbar>
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs mb={3}>
                <Box variant="Container" className="uploadBox uploadBoxSec">
                  <input
                    accept=".xlsx, .xls, .csv"
                    className="fileInput"
                    id="contained-button-file"
                    multiple
                    type="file"
                    onChange={(e) => {
                      handleFileUpload(e);
                      e.target.value = null;
                    }}
                  />
                  <label htmlFor="contained-button-file">
                    <Box>
                      <BiCloudUpload className="upladIcon" />
                      <Typography variant="h5">{ui.fileName || 'Upload'}</Typography>
                      <Typography variant="body2">
                        Supported file types: .xls, .xlsx, .csv
                      </Typography>
                    </Box>
                  </label>
                </Box>
              </Grid>
              <Grid xs="auto" item mb={3} sx={{ flexGrow: '1' }}>
                <Button
                  className="mainBtn downloadBtn downloadBtnSec otherBtn"
                  variant="contained"
                  onClick={handleDownloadTemplate}
                >
                  <BiCloudDownload className="downloadIcon" />
                  <Typography variant="subtitle1">Download Template</Typography>
                </Button>
              </Grid>
            </Grid>

            <Box textAlign="right" mb={3}>
              <Button variant="contained" className="mainBtn" onClick={() => setShowTable(true)}>
                Submit
              </Button>
            </Box>
            {showTable && <UploadLeadProspectTable />}
          </Scrollbar>

          <Dialog open={ui.open} onClose={() => setUi((prev) => ({ ...prev, open: false }))}>
            <DialogContent>
              <DialogContentText>{ui.msg}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUi((prev) => ({ ...prev, open: false }))}>Retry</Button>
            </DialogActions>
          </Dialog>
          <Loader openLoader={ui.loader} />
        </Box>
        {template.headers && (
          <CSVDownload headers={template.headers} data={template.data} target="_blank" />
        )}
        <ApiErrorMessage
          openApiError={ui.apiError}
          setOpenApiError={(val) => setUi((prev) => ({ ...prev, apiError: val }))}
          errorMessage={ui.errorMsg}
        />
      </Container>
    </Page>
  );
}

export default UploadLeadProspect;
